import React from 'react'

// "How the course score and pass are reported" (LMS Phase F scoring, owner
// decision 2026-10-15): how a course with several quizzes reports one score
// and one pass/fail to the LMS. The LMS service computes the result from the
// quizzes the learner took in the launch; this is the rule it uses. A preset
// sets it for every course of its connections (staff, the preset editor); a
// course can have its own on a connection (the course's LMS integration).
// The wire names are the service's (rapid-api-lms CourseScoring).

export const PASS_RULES = [
  ['pass_every_quiz', 'Pass every quiz', 'Passed only if the learner passes each quiz of the course. The package gives the LMS no pass mark of its own.'],
  ['overall_score', 'Overall score', 'Passed if the course score reaches the pass mark, even with a quiz failed. The package gives the LMS the same pass mark.']
]

export const SCORE_RULES = [
  ['combined_points', 'Combined points', 'Points earned over points possible across the quizzes: a longer quiz counts for more.'],
  ['average_of_quizzes', 'Average of quizzes', 'Each quiz counts the same, whatever its length.']
]

export const SCORING_DEFAULT = { pass_rule: 'pass_every_quiz', score_rule: 'combined_points', pass_mark: null }

const labelOf = (list, value) => (list.find(([v]) => v === value) || [value, value])[1]

// One line for a setting: "Pass every quiz · Combined points", or "Overall
// score, pass mark 80% · Average of quizzes".
export const describeScoring = scoring => {
  if (!scoring) return ''
  const pass =
    scoring.pass_rule === 'overall_score' && scoring.pass_mark !== null && scoring.pass_mark !== undefined
      ? `${labelOf(PASS_RULES, scoring.pass_rule)}, pass mark ${scoring.pass_mark}%`
      : labelOf(PASS_RULES, scoring.pass_rule)
  return `${pass} · ${labelOf(SCORE_RULES, scoring.score_rule)}`
}

// The setting as the editor holds it (the pass mark as typed text).
export const draftOf = scoring => {
  const s = scoring || SCORING_DEFAULT
  return {
    pass_rule: s.pass_rule || SCORING_DEFAULT.pass_rule,
    score_rule: s.score_rule || SCORING_DEFAULT.score_rule,
    pass_mark: s.pass_mark === null || s.pass_mark === undefined ? '' : String(s.pass_mark)
  }
}

// What a draft refuses before it is sent (the service checks the same):
// Overall score needs a pass mark of 0 to 100 with at most 2 decimals.
export const PASS_MARK_ERROR = 'Enter a pass mark from 0 to 100, with at most 2 decimals.'

const hasAtMostTwoDecimals = mark => Math.abs(mark * 100 - Math.round(mark * 100)) < 1e-6

export const draftError = draft => {
  if (draft.pass_rule !== 'overall_score') return null
  const text = String(draft.pass_mark).trim()
  const mark = Number(text)
  if (text === '' || !Number.isFinite(mark) || mark < 0 || mark > 100 || !hasAtMostTwoDecimals(mark)) return PASS_MARK_ERROR
  return null
}

// The draft as the API takes it: the pass mark only with Overall score.
export const scoringOf = draft => ({
  pass_rule: draft.pass_rule,
  score_rule: draft.score_rule,
  pass_mark: draft.pass_rule === 'overall_score' ? Number(String(draft.pass_mark).trim()) : null
})

// The two choices and the pass mark. errors are the server's refused fields
// (scoring.pass_rule, scoring.score_rule, scoring.pass_mark) and the draft's
// own pass mark error.
export const ScoreReportingFields = ({ draft, onChange, disabled, errors = {}, name = 'scoring' }) => {
  const set = patch => onChange({ ...draft, ...patch })
  const error = field =>
    errors[field] && (
      <div className="field-error" data-testid={`${name}-error-${field}`}>
        {errors[field]}
      </div>
    )
  return (
    <div data-testid={`${name}-fields`}>
      <div className="field">
        <label className="field-label">Pass rule</label>
        <div className="radio-group">
          {PASS_RULES.map(([value, label, desc]) => (
            <label key={value} className={`radio-option ${draft.pass_rule === value ? 'selected' : ''}`}>
              <input
                type="radio"
                name={`${name}-pass-rule`}
                value={value}
                checked={draft.pass_rule === value}
                disabled={disabled}
                data-testid={`${name}-pass-${value}`}
                onChange={() => set({ pass_rule: value })}
              />
              <div className="body">
                <div className="name">
                  {label}
                  {value === SCORING_DEFAULT.pass_rule && <span className="text-muted"> (default)</span>}
                </div>
                <div className="desc">{desc}</div>
              </div>
            </label>
          ))}
        </div>
        {error('pass_rule')}
      </div>
      {draft.pass_rule === 'overall_score' && (
        <div className="field">
          <label className="field-label" htmlFor={`${name}-pass-mark`}>
            Pass mark (%)
          </label>
          <input
            id={`${name}-pass-mark`}
            className="form-control form-control-sm"
            type="number"
            min={0}
            max={100}
            step="0.01"
            style={{ maxWidth: 120 }}
            value={draft.pass_mark}
            disabled={disabled}
            data-testid={`${name}-pass-mark`}
            onChange={e => set({ pass_mark: e.target.value })}
          />
          {error('pass_mark')}
        </div>
      )}
      <div className="field">
        <label className="field-label">Score</label>
        <div className="radio-group">
          {SCORE_RULES.map(([value, label, desc]) => (
            <label key={value} className={`radio-option ${draft.score_rule === value ? 'selected' : ''}`}>
              <input
                type="radio"
                name={`${name}-score-rule`}
                value={value}
                checked={draft.score_rule === value}
                disabled={disabled}
                data-testid={`${name}-score-${value}`}
                onChange={() => set({ score_rule: value })}
              />
              <div className="body">
                <div className="name">
                  {label}
                  {value === SCORING_DEFAULT.score_rule && <span className="text-muted"> (default)</span>}
                </div>
                <div className="desc">{desc}</div>
              </div>
            </label>
          ))}
        </div>
        {error('score_rule')}
      </div>
      <div className="field-help" data-testid={`${name}-help`}>
        A course with no quiz reports completion only. Each quiz counts the learner&apos;s best attempt across their launches of
        this course from this LMS; a quiz they have not taken scores nothing. Only quizzes a learner can reach in the course
        (assigned to an objective or attached to a step) count. Rebuild the course&apos;s package after a change so the LMS uses
        the same rule.
      </div>
    </div>
  )
}
